import React, { useEffect, useState } from 'react';
import {
	TextField,
	Button,
	Grid,
	Paper,
	MenuItem,
	Autocomplete,
	Box,
	Divider,
	Radio,
	RadioGroup,
	FormControlLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	CircularProgress,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	getToDoPrioritys,
	getAllTodoCategories,
	getChecklistGroupsForTodo,
} from 'core/API/todo';
import { getUsersByWorkplace, GetFrequencyList } from 'core/API/checklist';
import { assignmentWorkplaceUserOptions } from 'core/utils/enrichWorkplaceUsers';
import { getToDoScheduleById, updateToDoSchedule } from 'core/API/recurringTodo';
import RecurrencePicker from 'module/toDoPage/components/RecurrencePicker';
import {
	buildToDoSchedulePayload,
	DEFAULT_RECURRENCE_FORM,
	filterRecurringFrequencies,
	parseToDoScheduleToRecurrenceForm,
	RecurrenceFormState,
	validateRecurrenceForm,
} from 'module/toDoPage/utils/recurringTodoUtils';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import styles from '../addToDo/AddToDo.module.scss';
import { parsePriorityIdFromRef } from 'core/utils/todoScheduleEvent';
import { useToDoPermissions } from 'module/toDoPage/hooks/useToDoPermissions';
import { getCategoryIri } from 'module/toDoPage/utils/todoCategoryUtils';

type RouteParams = { id: string };

const EditToDoSchedule: React.FC = () => {
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const history = useHistory();
	const { id } = useParams<RouteParams>();
	const { canEdit: canEditToDo } = useToDoPermissions();
	const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1', 10);

	const [form, setForm] = useState({
		description: '',
		noteMessage: '',
		priorityId: 1,
		categoryIri: '',
	});
	const [assignmentType, setAssignmentType] = useState<'Person' | 'Group'>('Person');
	const [selectedValue, setSelectedValue] = useState<number | ''>('');
	const [recurrence, setRecurrence] = useState<RecurrenceFormState>(DEFAULT_RECURRENCE_FORM);
	const [recurrenceErrors, setRecurrenceErrors] = useState<Record<string, string>>({});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isFormDirty, setIsFormDirty] = useState(false);
	const [openBackDialog, setOpenBackDialog] = useState(false);
	const [isHydrated, setIsHydrated] = useState(false);

	const { data: frequencyList, isLoading: isFrequenciesLoading } = useQuery(
		['todo-frequencies', workplaceId],
		() => GetFrequencyList(),
		{ enabled: !!workplaceId }
	);
	const recurringFrequencyOptions = filterRecurringFrequencies(
		Array.isArray(frequencyList) ? frequencyList : []
	);

	const { data: scheduleData, isLoading: isLoadingSchedule, error: scheduleError } = useQuery(
		['to-do-schedule', id],
		() => getToDoScheduleById(Number(id)),
		{ enabled: !!id }
	);

	const { data: prioritysList, isLoading: isPrioritiesLoading } = useQuery(
		['todo-priorities', workplaceId],
		() => getToDoPrioritys(),
		{ enabled: !!workplaceId }
	);
	const { data: todoCategories, isLoading: isCategoriesLoading } = useQuery(
		['todo-categories', workplaceId],
		() => getAllTodoCategories(),
		{ enabled: !!workplaceId }
	);
	const { data: usersList, isLoading: isUsersLoading } = useQuery(
		['todo-users', workplaceId],
		() => getUsersByWorkplace(workplaceId, assignmentWorkplaceUserOptions),
		{ enabled: !!workplaceId }
	);
	const { data: groupsList, isLoading: isGroupsLoading } = useQuery(
		['todo-groups', workplaceId],
		() => getChecklistGroupsForTodo(),
		{ enabled: !!workplaceId }
	);

	useEffect(() => {
		if (!scheduleData || isHydrated) return;
		const schedule = scheduleData as Record<string, unknown>;
		const frequencyOptions =
			recurringFrequencyOptions.length > 0
				? recurringFrequencyOptions
				: filterRecurringFrequencies([]);

		setForm({
			description: String(schedule.description ?? ''),
			noteMessage: typeof schedule.note === 'string' ? schedule.note : '',
			priorityId: parsePriorityIdFromRef(schedule.priority) ?? 1,
			categoryIri: getCategoryIri(schedule.category),
		});

		if (schedule.assignmentType === 'group' || schedule.group) {
			setAssignmentType('Group');
			const group = schedule.group as Record<string, unknown> | undefined;
			setSelectedValue(Number(group?.id) || '');
		} else {
			setAssignmentType('Person');
			const assignee = schedule.assignee as Record<string, unknown> | undefined;
			setSelectedValue(Number(assignee?.id) || '');
		}

		setRecurrence(parseToDoScheduleToRecurrenceForm(schedule, frequencyOptions));
		setIsHydrated(true);
	}, [scheduleData, isHydrated, recurringFrequencyOptions]);

	const normalizedPriorities = Array.isArray((prioritysList as any)?.member)
		? (prioritysList as any).member
		: Array.isArray(prioritysList)
			? prioritysList
			: [];
	const normalizedCategories = Array.isArray(todoCategories) ? todoCategories : [];
	const normalizedUsers = Array.isArray(usersList) ? usersList : [];
	const normalizedGroups = Array.isArray((groupsList as any)?.member)
		? (groupsList as any).member
		: Array.isArray(groupsList)
			? groupsList
			: [];
	const selectedCategory =
		normalizedCategories.find((cat: any) => getCategoryIri(cat) === form.categoryIri) ?? null;

	const { mutate: saveSchedule, isLoading: isSaving } = useMutation(
		(payload: ReturnType<typeof buildToDoSchedulePayload>) =>
			updateToDoSchedule(Number(id), payload),
		{
			onSuccess: () => {
				showToast('success', 'Recurring to-do series updated successfully');
				queryClient.invalidateQueries(['to-do-schedules', workplaceId]);
				queryClient.invalidateQueries(['todos-categorized', workplaceId]);
				history.push(ROUTES.TO_DO);
			},
			onError: () => {
				showToast('error', 'Failed to update recurring to-do series');
			},
		}
	);

	const validateForm = () => {
		const nextErrors: Record<string, string> = {};
		if (!form.description.trim()) nextErrors.description = 'Description is required';
		if (!selectedValue) {
			nextErrors.assigned = `Please select a ${assignmentType === 'Person' ? 'person' : 'group'}`;
		}
		if (!form.categoryIri) nextErrors.category = 'Category is required';
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleSubmit = (e?: React.FormEvent) => {
		e?.preventDefault();
		if (!validateForm()) return;

		const nextRecurrenceErrors = validateRecurrenceForm(recurrence);
		setRecurrenceErrors(nextRecurrenceErrors);
		if (Object.keys(nextRecurrenceErrors).length > 0) return;

		saveSchedule(
			buildToDoSchedulePayload({
				description: form.description,
				note: form.noteMessage,
				priorityId: form.priorityId,
				categoryIri: form.categoryIri,
				assignmentType,
				selectedValue: Number(selectedValue),
				recurrence,
			})
		);
	};

	const handleBackClick = () => {
		if (isFormDirty) setOpenBackDialog(true);
		else history.push(ROUTES.TO_DO);
	};

	if (isLoadingSchedule) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (scheduleError || !scheduleData) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography color="error" mb={2}>
					Failed to load recurring to-do series.
				</Typography>
				<Button onClick={() => history.push(ROUTES.TO_DO)} style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}>
					Back to To-Dos
				</Button>
			</Box>
		);
	}

	if (!canEditToDo) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography color="error" mb={2}>
					You do not have permission to edit recurring to-do series.
				</Typography>
				<Button onClick={() => history.push(ROUTES.TO_DO)} style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}>
					Back to To-Dos
				</Button>
			</Box>
		);
	}

	return (
		<Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Button style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }} onClick={handleBackClick}>
					Back
				</Button>
				<Button
					style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', padding: '5px 12px' }}
					disabled={isSaving}
					onClick={() => handleSubmit()}
				>
					{isSaving ? 'Saving...' : 'Update Series'}
				</Button>
			</Box>

			<Divider sx={{ mb: 2 }} />
			<Typography variant="h6" mb={1} className={styles.schedulerTitle}>
				Edit Recurring To-Do Series
			</Typography>
			<Typography variant="body2" color="text.secondary" mb={2}>
				Changes apply to the entire recurring series and future generated to-dos.
			</Typography>

			<form onSubmit={handleSubmit}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Box className={styles.selectionBox}>
							<RadioGroup
								row
								value={assignmentType}
								onChange={(e) => {
									setAssignmentType(e.target.value as 'Person' | 'Group');
									setSelectedValue('');
									setIsFormDirty(true);
								}}
							>
								<FormControlLabel value="Person" control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} />} label="Person" />
								<FormControlLabel value="Group" control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} />} label="Group" />
							</RadioGroup>
							<TextField
								select
								label={assignmentType === 'Person' ? 'Select Person' : 'Select Group'}
								value={selectedValue}
								onChange={(e) => {
									setSelectedValue(Number(e.target.value));
									setIsFormDirty(true);
								}}
								fullWidth
								required
								disabled={assignmentType === 'Person' ? isUsersLoading : isGroupsLoading}
							>
								<MenuItem value="" disabled>
									{assignmentType === 'Person' ? 'Select Person' : 'Select Group'}
								</MenuItem>
								{assignmentType === 'Person'
									? normalizedUsers.map((user: any) => (
											<MenuItem key={user.id} value={user.id}>
												{`${user.firstName || ''} ${user.lastName || ''}`.trim()}
											</MenuItem>
										))
									: normalizedGroups.map((group: any) => (
											<MenuItem key={group.id} value={group.id}>
												{group.label || group.name}
											</MenuItem>
										))}
							</TextField>
							{errors.assigned && (
								<Typography variant="caption" color="error">
									{errors.assigned}
								</Typography>
							)}
						</Box>
					</Grid>

					<Grid item xs={12}>
						<RecurrencePicker
							value={recurrence}
							onChange={(value) => {
								setRecurrence(value);
								setIsFormDirty(true);
								setRecurrenceErrors({});
							}}
							frequencyOptions={recurringFrequencyOptions}
							disabled={isSaving || isFrequenciesLoading}
							errors={recurrenceErrors}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label="Description"
							fullWidth
							required
							value={form.description}
							onChange={(e) => {
								setForm((prev) => ({ ...prev, description: e.target.value }));
								setIsFormDirty(true);
							}}
							inputProps={{ maxLength: 250 }}
						/>
					</Grid>

					<Grid item xs={6}>
						<TextField
							select
							label="Priority"
							fullWidth
							value={form.priorityId}
							onChange={(e) => {
								setForm((prev) => ({ ...prev, priorityId: Number(e.target.value) }));
								setIsFormDirty(true);
							}}
							disabled={isPrioritiesLoading}
						>
							{normalizedPriorities.map((priority: any) => (
								<MenuItem key={priority.id} value={priority.id}>
									{priority.label ?? priority.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					<Grid item xs={6}>
						<Autocomplete
							options={normalizedCategories}
							getOptionLabel={(option: any) => option?.label || ''}
							isOptionEqualToValue={(option: any, value: any) =>
								getCategoryIri(option) === getCategoryIri(value)
							}
							value={selectedCategory}
							onChange={(_, value: any) => {
								setForm((prev) => ({ ...prev, categoryIri: value ? getCategoryIri(value) : '' }));
								setIsFormDirty(true);
							}}
							disabled={isCategoriesLoading}
							renderInput={(params) => (
								<TextField {...params} label="Category" required error={!!errors.category} helperText={errors.category} />
							)}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label="Notes"
							multiline
							rows={3}
							fullWidth
							value={form.noteMessage}
							onChange={(e) => {
								setForm((prev) => ({ ...prev, noteMessage: e.target.value }));
								setIsFormDirty(true);
							}}
							inputProps={{ maxLength: 500 }}
						/>
					</Grid>
				</Grid>
			</form>

			<Dialog open={openBackDialog} onClose={() => setOpenBackDialog(false)}>
				<DialogTitle>Unsaved Changes</DialogTitle>
				<DialogContent>You have unsaved changes. Would you like to save before leaving?</DialogContent>
				<DialogActions>
					<Button onClick={() => history.push(ROUTES.TO_DO)}>Discard</Button>
					<Button onClick={() => { setOpenBackDialog(false); handleSubmit(); }} style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
};

export default EditToDoSchedule;
